import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/components/theme-provider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { WebSocketProvider } from "@/context/WebSocketContext";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import ChargersPage from "@/pages/ChargersPage";
import ChargerDetailPage from "@/pages/ChargerDetailPage";
import SessionsPage from "@/pages/SessionsPage";
import SessionDetailPage from "@/pages/SessionDetailPage";
import TariffsPage from "@/pages/TariffsPage";
import AlarmsPage from "@/pages/AlarmsPage";
import SettingsPage from "@/pages/SettingsPage";
import ChargerRequestsPage from "@/pages/ChargerRequestsPage";
import ChargerRequestDetailPage from "@/pages/ChargerRequestDetailPage";
import AdministrationPage from "@/pages/AdministrationPage";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <WebSocketProvider>{children}</WebSocketProvider>;
};

const AppRoutes = () => {
  const { isAuthenticated, loading, can } = useAuth();
  const defaultPath = can('operations.view') ? '/' : can('requests.view') ? '/charger-requests' : '/chargers';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to={defaultPath} replace /> : <LoginPage />}
      />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <DashboardPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/chargers"
        element={
          <ProtectedRoute>
            <Layout>
              <ChargersPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/chargers/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <ChargerDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sessions"
        element={
          <ProtectedRoute>
            <Layout>
              <SessionsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/sessions/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <SessionDetailPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/tariffs"
        element={
          <ProtectedRoute>
            <Layout>
              <TariffsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/alarms"
        element={
          <ProtectedRoute>
            <Layout>
              <AlarmsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/charger-requests"
        element={<ProtectedRoute><Layout><ChargerRequestsPage /></Layout></ProtectedRoute>}
      />
      <Route
        path="/charger-requests/:id"
        element={<ProtectedRoute><Layout><ChargerRequestDetailPage /></Layout></ProtectedRoute>}
      />
      <Route
        path="/administration"
        element={<ProtectedRoute><Layout><AdministrationPage /></Layout></ProtectedRoute>}
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Layout>
              <SettingsPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to={defaultPath} replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <TooltipProvider delayDuration={300}>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="bottom-right" offset={16} richColors />
          </BrowserRouter>
        </AuthProvider>
      </TooltipProvider>
    </ThemeProvider>
  );
}

export default App;
